/**
 * Zoom and pan, as arithmetic on an SVG viewBox.
 *
 * The whole of BEST's "canvas": there is no canvas framework here and there is
 * not going to be one. A viewBox is four numbers, the browser does the
 * transform, and every box stays a real DOM node that can be clicked, focused
 * and read by a screen reader — which a canvas would give up.
 *
 * Pure and exported so the arithmetic is tested apart from anything that draws.
 * A zoom that does not hold its anchor point still LOOKS like a zoom; it just
 * drifts, and nobody can say why.
 */

export interface ViewBox {
  x: number
  y: number
  width: number
  height: number
}

/** Zoomed out far enough to be useless, and in far enough to be useless. */
export const MIN_SCALE = 0.2
export const MAX_SCALE = 4

/**
 * The closest zoom in screen terms: one diagram unit drawn this many pixels
 * wide. At 2 a box of the default 184 units is 368 pixels, so its 13-unit
 * label is 26 pixels tall - past readable, and no further, so a zoom cannot be
 * driven into one letter.
 */
export const MAX_PIXELS_PER_UNIT = 2

/**
 * The narrowest viewBox a zoom may reach.
 *
 * The SMALLER of the two limits, so that whichever lets you closer wins: a
 * small diagram keeps its old MAX_SCALE limit (four times the fitted whole is
 * already well past natural size), and a wide one may still be zoomed until a
 * box reads at twice its natural size. With no pixel width - before the pane is
 * laid out, or under jsdom - only the old limit applies.
 *
 * Measured on WIDTH, which is the drawn scale only when the box has the pane's
 * shape - which is why the canvas fits through `fitToPane`.
 *
 * @param[in] base - ViewBox : the fitted box
 * @param[in] pixelWidth - number : the pane's on-screen width, 0 when unknown
 * @return number
 */
export function narrowestWidth(base: ViewBox, pixelWidth: number): number {
  const byWhole = base.width / MAX_SCALE
  if (!(pixelWidth > 0)) return byWhole
  return Math.min(byWhole, pixelWidth / MAX_PIXELS_PER_UNIT)
}

/**
 * A view showing the diagram at its natural size, one unit to one pixel.
 *
 * Centred on `centre`, and the same shape as the pane, so a click on "actual
 * size" goes straight to readable boxes wherever you were looking - from a
 * fitted wide diagram that is twenty zoom clicks otherwise.
 *
 * @param[in] centre - {x, y} : the diagram point to put in the middle
 * @param[in] pixelWidth - number : the pane's on-screen width
 * @param[in] pixelHeight - number : the pane's on-screen height
 * @return ViewBox
 */
export function actualSize(
  centre: { x: number; y: number },
  pixelWidth: number,
  pixelHeight: number,
): ViewBox {
  const width = Math.max(1, pixelWidth)
  const height = Math.max(1, pixelHeight)
  return { x: centre.x - width / 2, y: centre.y - height / 2, width, height }
}

/** The whole diagram, with a little air around it. */
export function fitViewBox(width: number, height: number, margin = 24): ViewBox {
  return {
    x: -margin,
    y: -margin,
    width: Math.max(1, width + margin * 2),
    height: Math.max(1, height + margin * 2),
  }
}

/**
 * Widen a box, about its centre, to the pane's shape.
 *
 * An SVG letterboxes a viewBox that is not the shape of the element showing it
 * (`preserveAspectRatio` meet), and then the box's WIDTH no longer says how
 * big anything is drawn: a tall diagram in a wide pane is scaled by its height.
 * Everything here measures zoom by width - the limits, "actual size", the
 * pan's units per pixel - so the canvas fits through this first, and width is
 * the true scale everywhere. Issue #93 made it matter: wrapping turned a wide
 * diagram into a tall one, and at the zoom limit its boxes drew at a fifth of
 * the size the arithmetic claimed.
 *
 * Only ever grows the box, so the whole diagram stays in view. Unchanged when
 * the pane has not been measured (0, as under jsdom).
 *
 * @param[in] box - ViewBox : the fitted diagram
 * @param[in] pixelWidth - number : the pane's on-screen width
 * @param[in] pixelHeight - number : the pane's on-screen height
 * @return ViewBox
 */
export function fitToPane(box: ViewBox, pixelWidth: number, pixelHeight: number): ViewBox {
  if (!(pixelWidth > 0) || !(pixelHeight > 0)) return box
  const shape = pixelHeight / pixelWidth
  if (box.height / box.width > shape) {
    const width = box.height / shape
    return { ...box, x: box.x - (width - box.width) / 2, width }
  }
  const height = box.width * shape
  return { ...box, y: box.y - (height - box.height) / 2, height }
}

/**
 * Zoom by `factor` about a point, keeping that point where it is on screen.
 *
 * `anchor` is in DIAGRAM coordinates, and it is what makes zooming feel like
 * zooming: scaling about the origin instead slides whatever you were looking at
 * off the edge. A factor above 1 zooms IN, which means a SMALLER viewBox.
 *
 * Zooming OUT is clamped against `base`, the fitted box: "a fifth of the whole
 * diagram". Zooming IN is clamped by whichever lets you closer of MAX_SCALE times
 * the fitted whole, and a box drawn at MAX_PIXELS_PER_UNIT times its natural
 * size - see `narrowestWidth`. The second is what makes a wide diagram readable
 * at all: issue #93's few hundred requirements fitted into a pane put a box at
 * about 7 pixels, and four times that is still not something anyone can read.
 *
 * @param[in] box - ViewBox : where the view is now
 * @param[in] factor - number : >1 zooms in, <1 zooms out
 * @param[in] anchor - {x, y} : the diagram point to hold still
 * @param[in] base - ViewBox : the fitted box the scale limits are relative to
 * @param[in] pixelWidth - number : the pane's on-screen width, 0 when unknown
 * @return ViewBox
 */
export function zoomAt(
  box: ViewBox,
  factor: number,
  anchor: { x: number; y: number },
  base: ViewBox,
  pixelWidth = 0,
): ViewBox {
  const wanted = box.width / factor
  // width is inversely proportional to scale, so the widest allowed box is the
  // one at MIN_SCALE and the narrowest is the closest zoom allowed.
  const width = Math.min(base.width / MIN_SCALE, Math.max(narrowestWidth(base, pixelWidth), wanted))
  const applied = width / box.width
  const height = box.height * applied
  return {
    // The anchor sits at the same fraction across the box before and after, so
    // its offset from the box origin scales with the box.
    x: anchor.x - (anchor.x - box.x) * applied,
    y: anchor.y - (anchor.y - box.y) * applied,
    width,
    height,
  }
}

/**
 * Move the view by a distance in diagram coordinates.
 *
 * Dragging RIGHT should reveal what is to the LEFT, so the box moves the other
 * way — hence the subtraction. Getting that sign wrong produces a diagram that
 * pans the wrong way, which is the kind of thing that gets described as
 * "the diagram feels broken" rather than reported precisely.
 */
export function panBy(box: ViewBox, dx: number, dy: number): ViewBox {
  return { ...box, x: box.x - dx, y: box.y - dy }
}

/** How zoomed in the view is, 1 being the fitted whole. */
export function scaleOf(box: ViewBox, base: ViewBox): number {
  return base.width / box.width
}
